import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import './TaskCompliance.less';
import { getCalendarWeekdayOffset, orderCalendarWeekDays } from '@/constants/calendar';

const DAYS_OF_WEEK = orderCalendarWeekDays(['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']);

const ComplianceCalendar = ({
  dailyTasks,
  userTimezone,
  onDayClick = null,
  currentMonth,
  onMonthChange,
}) => {
  const trans = useSelector(translator);
  const tz = userTimezone;

  // Pre-index dailyTasks by date for O(1) lookups
  const tasksByDate = useMemo(() => {
    const map = {};
    dailyTasks.forEach((d) => {
      map[d.date] = d;
    });
    return map;
  }, [dailyTasks]);

  // Generate calendar days for current month
  const calendarDays = useMemo(() => {
    const today = moment().tz(tz);
    const todayStr = today.format('YYYY-MM-DD');
    const daysInMonth = currentMonth.daysInMonth();
    const leadingDays = getCalendarWeekdayOffset(currentMonth.clone().startOf('month').day());

    const days = [];

    for (let i = 0; i < leadingDays; i++) {
      days.push({ blank: true, key: `blank-${i}` });
    }

    // Fill in the days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = currentMonth.clone().date(day).format('YYYY-MM-DD');
      const dayData = tasksByDate[dateStr];

      // Determine day status
      // Red = any task missed, Green = all completed, Ongoing = in progress, Blue = scheduled (future), Empty = no tasks
      let status = '';

      if (dayData && dayData.tasks && dayData.tasks.length > 0) {
        const anyMissed = dayData.tasks.some((t) => t.status === 'expired');
        const allCompleted = dayData.tasks.every((t) => t.status === 'completed');
        const anyOngoing = dayData.tasks.some((t) => t.status === 'ongoing');
        const anyScheduled = dayData.tasks.some((t) => t.status === 'scheduled');

        if (anyMissed) {
          status = 'expired';
        } else if (allCompleted) {
          status = 'completed';
        } else if (anyOngoing || anyScheduled) {
          status = 'scheduled';
        }
      }

      const isToday = todayStr === dateStr;
      const isFuture = dateStr > todayStr;

      days.push({
        day,
        dateStr,
        status,
        isToday,
        isFuture,
        tasks: dayData?.tasks || [],
        key: `day-${day}`,
      });
    }

    // Pad trailing blanks so every month renders exactly 6 rows (42 cells)
    while (days.length < 42) {
      days.push({ blank: true, key: `blank-end-${days.length}` });
    }
    return days;
  }, [currentMonth, tasksByDate, tz]);

  // Handle day click
  const handleDayClick = (dayData) => {
    if (dayData && !dayData.blank && onDayClick) {
      onDayClick(dayData.dateStr, dayData.tasks);
    }
  };

  // Handle keyboard events for day selection
  const handleKeyDown = (e, dayData) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleDayClick(dayData);
    }
  };

  // Navigate to previous month
  const handlePreviousMonth = () => {
    onMonthChange(currentMonth.clone().subtract(1, 'month'));
  };

  // Navigate to next month
  const handleNextMonth = () => {
    onMonthChange(currentMonth.clone().add(1, 'month'));
  };

  const monthName = currentMonth.format('MMMM');
  const year = currentMonth.format('YYYY');

  return (
    <div className="compliance-calendar-container">
      {/* Calendar Navigation */}
      <div className="compliance-calendar-navigation">
        <button
          className="calendar-nav-button"
          onClick={handlePreviousMonth}
          aria-label={trans('PREVIOUS_MONTH')}
        >
          &#8249;
        </button>
        <div className="compliance-calendar-header">
          <div className="compliance-selectyearmonth">
            {trans(monthName.toUpperCase()) || monthName}
          </div>
          <div className="compliance-selectyearmonth">{year}</div>
        </div>
        <button
          className="calendar-nav-button"
          onClick={handleNextMonth}
          aria-label={trans('NEXT_MONTH')}
        >
          &#8250;
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="compliance-calendar-date-container">
        <div className="compliance-calendar-grid">
          {/* Days of week header */}
          {DAYS_OF_WEEK.map((day) => (
            <div key={day} className="compliance-calendar-day-header">
              {day}
            </div>
          ))}

          {/* Calendar days */}
          {calendarDays.map((dayData) => {
            if (dayData.blank) {
              return (
                <div key={dayData.key} className="compliance-calendar-day compliance-blank"></div>
              );
            }

            return (
              <div
                key={dayData.key}
                className={[
                  'compliance-calendar-day',
                  dayData.status && `compliance-date-${dayData.status}`,
                  dayData.isToday && 'compliance-today',
                  dayData.isFuture && !dayData.status && 'compliance-future-empty',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => handleDayClick(dayData)}
                onKeyDown={(e) => handleKeyDown(e, dayData)}
                role="button"
                tabIndex={0}
                aria-label={`${moment(dayData.dateStr).tz(tz).format('MMMM D YYYY')}, ${dayData.tasks.length} tasks, ${dayData.status || 'no tasks'}`}
              >
                {dayData.day}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="calendar-legend">
        <div className="calendar-legend-items">
          <div className="calendar-legend-item legend-item--completed">
            <span className="calendar-legend-dot legend-dot-completed"></span>
            <span className="calendar-legend-label">{trans('COMPLETED')}</span>
          </div>
          <div className="calendar-legend-item legend-item--scheduled">
            <span className="calendar-legend-dot legend-dot-scheduled"></span>
            <span className="calendar-legend-label">{trans('SCHEDULED')}</span>
          </div>
          <div className="calendar-legend-item legend-item--expired">
            <span className="calendar-legend-dot legend-dot-expired"></span>
            <span className="calendar-legend-label">{trans('MISSED')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

ComplianceCalendar.propTypes = {
  dailyTasks: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      tasks: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          status: PropTypes.string,
          time: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  userTimezone: PropTypes.string.isRequired,
  onDayClick: PropTypes.func,
  currentMonth: PropTypes.instanceOf(moment).isRequired,
  onMonthChange: PropTypes.func.isRequired,
};

export default ComplianceCalendar;
